import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarCheck, Plus, Clock, Phone, User, CheckCircle2, XCircle, PlayCircle, Trash2, Edit3, Pill, Printer, DollarSign, Receipt } from "lucide-react";

const STATUS = {
  scheduled: { l: "مجدول", c: "bg-slate-100 text-slate-700 border-slate-200" },
  confirmed: { l: "مؤكد", c: "bg-blue-100 text-blue-700 border-blue-200" },
  in_progress: { l: "قيد الكشف", c: "bg-amber-100 text-amber-800 border-amber-300" },
  done: { l: "مكتمل", c: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { l: "ملغى", c: "bg-red-50 text-red-600 border-red-200" },
  no_show: { l: "لم يحضر", c: "bg-red-50 text-red-600 border-red-200" },
};

export default function ClinicAppointments() {
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusDlg, setStatusDlg] = useState(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [rxOn, setRxOn] = useState(false);
  const [rx, setRx] = useState({ diagnosis: "", drugs: [{ name: "", dosage: "", duration: "", notes: "" }], notes: "" });
  const [rxTemplates, setRxTemplates] = useState([]);
  const [saveTplName, setSaveTplName] = useState("");
  const [quickAdd, setQuickAdd] = useState(false);
  const [payDlg, setPayDlg] = useState(null); // appointment being paid
  const [payForm, setPayForm] = useState({ payment_type: "cash", paid_amount: "", fee_override: "", notes: "" });
  const [newPatient, setNewPatient] = useState({ name: "", phone: "", gender: "other" });
  const [form, setForm] = useState({ patient_id: "", doctor_name: "", date: date, time: "09:00", duration_min: 30, reason: "", status: "scheduled", fee: 0, notes: "" });

  const load = async () => {
    try {
      const [a, p, t] = await Promise.all([api.get(`/clinic/appointments?date=${date}`), api.get("/clinic/patients"), api.get("/clinic/rx-templates").catch(() => ({ data: [] }))]);
      setAppts(a.data); setPatients(p.data); setRxTemplates(t.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const openNew = () => { setEditing(null); setForm({ patient_id: "", doctor_name: "", date: date, time: "09:00", duration_min: 30, reason: "", status: "scheduled", fee: 0, notes: "" }); setDlg(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setDlg(true); };

  const save = async () => {
    let patientId = form.patient_id;
    // Quick-add: create the patient inline if user filled the mini form
    if (quickAdd) {
      if (!newPatient.name.trim()) return toast.error("اسم المريض الجديد مطلوب");
      try {
        const { data } = await api.post("/clinic/patients", newPatient);
        patientId = data.id;
        toast.success("تم إنشاء ملف المريض");
      } catch (e) { return toast.error(formatErr(e)); }
    }
    if (!patientId) return toast.error("اختر مريضاً أو أضف مريضاً جديداً");
    if (!form.date || !form.time) return toast.error("التاريخ والوقت مطلوبان");
    try {
      const payload = { ...form, patient_id: patientId };
      if (editing) await api.put(`/clinic/appointments/${editing.id}`, payload);
      else await api.post("/clinic/appointments", payload);
      toast.success("تم الحفظ");
      setDlg(false); setQuickAdd(false); setNewPatient({ name: "", phone: "", gender: "other" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (a) => {
    if (!window.confirm(`حذف موعد ${a.patient_name}؟`)) return;
    try { await api.delete(`/clinic/appointments/${a.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const changeStatus = async (a, status) => {
    if (status === "done") { setStatusDlg({ ...a, target: status }); setStatusNotes(""); setRxOn(false); setRx({ diagnosis: "", drugs: [{ name: "", dosage: "", duration: "", notes: "" }], notes: "" }); return; }
    try { await api.post(`/clinic/appointments/${a.id}/status`, { status }); toast.success("تم التحديث"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const finishVisit = async () => {
    try {
      await api.post(`/clinic/appointments/${statusDlg.id}/status`, { status: "done", notes: statusNotes });
      // Save prescription if enabled and has drugs
      let rxId = null;
      if (rxOn) {
        const cleanDrugs = rx.drugs.filter(d => (d.name || "").trim());
        if (cleanDrugs.length > 0) {
          const { data } = await api.post("/clinic/prescriptions", {
            appointment_id: statusDlg.id, patient_id: statusDlg.patient_id,
            doctor_name: statusDlg.doctor_name || "", diagnosis: rx.diagnosis,
            drugs: cleanDrugs, notes: rx.notes,
          });
          rxId = data.id;
        }
      }
      toast.success("تم إنهاء الزيارة");
      if (rxId && window.confirm("طباعة الوصفة الآن؟")) await printRx(rxId);
      setStatusDlg(null); load();
    }
    catch (e) { toast.error(formatErr(e)); }
  };

  const printRx = async (rxId) => {
    try {
      const { data } = await api.get(`/clinic/prescriptions/${rxId}`);
      const win = window.open("", "_blank");
      if (!win) return;
      const drugsRows = (data.drugs || []).map((d, i) => `<tr><td>${i + 1}</td><td><b>${d.name}</b></td><td>${d.dosage || "—"}</td><td>${d.duration || "—"}</td><td>${d.notes || ""}</td></tr>`).join("");
      win.document.write(`<html dir="rtl"><head><title>وصفة طبية</title><style>
        body{font-family:'Cairo',sans-serif;padding:32px;max-width:750px;margin:auto;color:#0f172a}
        .head{border-bottom:3px double #0284c7;padding-bottom:12px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:start}
        .clinic h1{color:#0284c7;margin:0 0 4px;font-size:22px}
        .clinic small{color:#64748b;display:block}
        .rx{width:64px;height:64px;background:#0284c7;color:#fff;border-radius:12px;display:grid;place-items:center;font-size:36px;font-weight:bold;font-family:Georgia,serif}
        .patient{background:#f1f5f9;padding:10px;border-radius:8px;margin-bottom:12px;font-size:13px;display:grid;grid-template-columns:1fr 1fr;gap:6px}
        .dx{padding:8px;background:#fef3c7;border-right:4px solid #d97706;margin-bottom:12px;font-size:14px}
        table{width:100%;border-collapse:collapse;margin:12px 0}
        th{background:#0284c7;color:#fff;padding:6px;font-size:12px}
        td{border-bottom:1px solid #e2e8f0;padding:8px;font-size:13px}
        .foot{margin-top:40px;display:flex;justify-content:space-between;font-size:12px;color:#64748b}
        .sig{border-top:1px solid #0f172a;padding-top:4px;width:200px;text-align:center}
        @media print{@page{margin:20mm}}
      </style></head><body>
      <div class="head">
        <div class="clinic"><h1>${data.company?.name || ""}</h1><small>${data.company?.phone || ""} · ${data.company?.address || ""}</small></div>
        <div class="rx">℞</div>
      </div>
      <div class="patient">
        <div><b>المريض:</b> ${data.patient_name}</div>
        <div><b>الهاتف:</b> ${data.patient_phone || "—"}</div>
        <div><b>التاريخ:</b> ${new Date(data.created_at).toLocaleDateString("ar-EG")}</div>
        <div><b>الطبيب:</b> ${data.doctor_name || "—"}</div>
      </div>
      ${data.diagnosis ? `<div class="dx"><b>التشخيص:</b> ${data.diagnosis}</div>` : ""}
      <table><thead><tr><th>#</th><th>الدواء</th><th>الجرعة</th><th>المدة</th><th>ملاحظات</th></tr></thead><tbody>${drugsRows}</tbody></table>
      ${data.notes ? `<div style="font-size:12px;color:#475569;padding:8px;background:#f8fafc;border-radius:6px">${data.notes}</div>` : ""}
      <div class="foot"><div>وصفة رقم: ${data.id.slice(0, 8).toUpperCase()}</div><div class="sig">توقيع الطبيب</div></div>
      </body></html>`);
      win.document.close(); setTimeout(() => win.print(), 500);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const addDrug = () => setRx({ ...rx, drugs: [...rx.drugs, { name: "", dosage: "", duration: "", notes: "" }] });
  const updDrug = (i, patch) => { const d = [...rx.drugs]; d[i] = { ...d[i], ...patch }; setRx({ ...rx, drugs: d }); };
  const rmDrug = (i) => setRx({ ...rx, drugs: rx.drugs.filter((_, idx) => idx !== i) });

  const openPay = (a) => { setPayDlg(a); setPayForm({ payment_type: "cash", paid_amount: String(a.fee || 0), fee_override: String(a.fee || 0), notes: "" }); };
  const doCheckout = async () => {
    const fee = parseFloat(payForm.fee_override) || 0;
    if (fee <= 0) return toast.error("أدخل قيمة الرسوم");
    const paid = payForm.payment_type === "credit" ? 0 : (parseFloat(payForm.paid_amount) || fee);
    try {
      const { data } = await api.post(`/clinic/appointments/${payDlg.id}/checkout`, {
        payment_type: payForm.payment_type,
        paid_amount: paid, fee_override: fee, notes: payForm.notes,
      });
      toast.success(`فاتورة #${data.invoice_number} — دُفع ${data.paid.toLocaleString("ar-EG")}`);
      setPayDlg(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadTemplate = (tpl) => {
    setRx({
      diagnosis: tpl.diagnosis || "",
      drugs: (tpl.drugs && tpl.drugs.length ? tpl.drugs : [{ name: "", dosage: "", duration: "", notes: "" }]).map(d => ({ ...d })),
      notes: tpl.notes || "",
    });
    toast.success(`تم تحميل قالب: ${tpl.name}`);
  };
  const saveAsTemplate = async () => {
    if (!saveTplName.trim()) return toast.error("اكتب اسماً للقالب");
    const clean = rx.drugs.filter(d => (d.name || "").trim());
    if (clean.length === 0) return toast.error("أضف دواءً واحداً على الأقل");
    try {
      const { data } = await api.post("/clinic/rx-templates", { name: saveTplName, diagnosis: rx.diagnosis, drugs: clean, notes: rx.notes });
      setRxTemplates([...rxTemplates, data]);
      setSaveTplName("");
      toast.success("تم حفظ القالب");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delTemplate = async (tpl) => {
    if (!window.confirm(`حذف قالب "${tpl.name}"؟`)) return;
    try { await api.delete(`/clinic/rx-templates/${tpl.id}`); setRxTemplates(rxTemplates.filter(t => t.id !== tpl.id)); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="appts-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><CalendarCheck className="w-6 h-6 text-primary" /> مواعيد العيادة</h1>
          <p className="text-sm text-muted-foreground mt-1">إدارة المواعيد وسجلات الزيارات</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto" data-testid="date-filter" />
          <Button onClick={openNew} className="gap-2" data-testid="new-appt-btn"><Plus className="w-4 h-4" /> موعد جديد</Button>
        </div>
      </div>

      {appts.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <CalendarCheck className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد مواعيد في هذا اليوم</div>
        </div>
      ) : (
        <div className="space-y-2">
          {appts.map(a => {
            const s = STATUS[a.status] || STATUS.scheduled;
            return (
              <div key={a.id} className={`p-3 rounded-lg border-2 flex items-center gap-3 ${s.c}`} data-testid={`appt-${a.id}`}>
                <div className="text-center w-20 flex-shrink-0">
                  <div className="text-lg font-bold font-mono">{a.time}</div>
                  <div className="text-[10px] text-muted-foreground">{a.duration_min} د</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center gap-1.5 flex-wrap"><User className="w-3.5 h-3.5" /> {a.patient_name} <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.c} border`}>{s.l}</span></div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                    {a.patient_phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" /> {a.patient_phone}</span>}
                    {a.doctor_name && <span>د. {a.doctor_name}</span>}
                    {a.reason && <span className="truncate max-w-xs">— {a.reason}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {a.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => changeStatus(a, "confirmed")} className="h-7 text-xs" data-testid={`confirm-${a.id}`}><CheckCircle2 className="w-3 h-3 me-1" /> تأكيد</Button>}
                  {["scheduled","confirmed"].includes(a.status) && <Button size="sm" variant="outline" onClick={() => changeStatus(a, "in_progress")} className="h-7 text-xs" data-testid={`start-${a.id}`}><PlayCircle className="w-3 h-3 me-1" /> بدء</Button>}
                  {a.status === "in_progress" && <Button size="sm" onClick={() => changeStatus(a, "done")} className="h-7 text-xs" data-testid={`finish-${a.id}`}><CheckCircle2 className="w-3 h-3 me-1" /> إنهاء</Button>}
                  {a.status === "done" && a.prescription_id && <Button size="sm" variant="outline" onClick={() => printRx(a.prescription_id)} className="h-7 text-xs" data-testid={`print-rx-${a.id}`}><Printer className="w-3 h-3 me-1" /> الوصفة</Button>}
                  {!a.invoice_id && (a.fee > 0 || a.status === "done") && <Button size="sm" onClick={() => openPay(a)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-${a.id}`}><DollarSign className="w-3 h-3 me-1" /> قبض</Button>}
                  {a.invoice_id && <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded flex items-center gap-1"><Receipt className="w-3 h-3" /> #{a.invoice_number}</span>}
                  {["scheduled","confirmed"].includes(a.status) && <Button size="sm" variant="outline" onClick={() => changeStatus(a, "no_show")} className="h-7 text-xs text-red-600"><XCircle className="w-3 h-3" /></Button>}
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)}><Edit3 className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New/Edit Dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" data-testid="appt-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل موعد" : "موعد جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>المريض *</Label>
              {!quickAdd ? (
                <>
                  <div className="flex gap-2">
                    <select className="flex-1 h-10 px-2 border rounded bg-background text-sm" value={form.patient_id} onChange={e => setForm({ ...form, patient_id: e.target.value })} data-testid="appt-patient">
                      <option value="">— اختر —</option>
                      {patients.map(p => <option key={p.id} value={p.id}>{p.name} {p.phone ? `(${p.phone})` : ""}</option>)}
                    </select>
                    <Button type="button" variant="outline" size="sm" onClick={() => setQuickAdd(true)} data-testid="quick-add-patient-btn"><Plus className="w-4 h-4 me-1" /> جديد</Button>
                  </div>
                  {patients.length === 0 && <p className="text-[10px] text-muted-foreground mt-1">لا يوجد مرضى — اضغط "جديد" لإضافة مريض أثناء الحجز</p>}
                </>
              ) : (
                <div className="p-3 border rounded bg-primary/5 space-y-2" data-testid="quick-add-form">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-primary">إضافة سريعة — سيُحفظ الملف تلقائياً</div>
                    <button type="button" onClick={() => setQuickAdd(false)} className="text-xs text-muted-foreground hover:text-foreground">← اختر موجوداً</button>
                  </div>
                  <Input value={newPatient.name} onChange={e => setNewPatient({ ...newPatient, name: e.target.value })} placeholder="اسم المريض *" data-testid="qa-name" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newPatient.phone} onChange={e => setNewPatient({ ...newPatient, phone: e.target.value })} placeholder="الهاتف" data-testid="qa-phone" />
                    <Select value={newPatient.gender} onValueChange={v => setNewPatient({ ...newPatient, gender: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="male">ذكر</SelectItem><SelectItem value="female">أنثى</SelectItem><SelectItem value="other">آخر</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>التاريخ</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>الوقت</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></div>
              <div><Label>المدة (د)</Label><Input type="number" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} /></div>
            </div>
            <div><Label>الطبيب</Label><Input value={form.doctor_name} onChange={e => setForm({ ...form, doctor_name: e.target.value })} placeholder="د. ..." /></div>
            <div><Label>سبب الزيارة</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="كشف، متابعة، فحوصات..." /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>الرسوم</Label><Input type="number" value={form.fee} onChange={e => setForm({ ...form, fee: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>الحالة</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-appt-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish visit dialog */}
      <Dialog open={!!statusDlg} onOpenChange={o => !o && setStatusDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="finish-visit-dialog">
          <DialogHeader><DialogTitle>إنهاء الزيارة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm"><span className="font-bold">المريض:</span> {statusDlg?.patient_name}</div>
            <div>
              <Label>ملاحظات الزيارة / التشخيص العام</Label>
              <Textarea rows={3} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="ملاحظات الفحص..." data-testid="visit-notes" />
            </div>

            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm font-bold text-primary cursor-pointer">
                <input type="checkbox" checked={rxOn} onChange={e => setRxOn(e.target.checked)} data-testid="enable-rx" />
                <Pill className="w-4 h-4" /> إضافة وصفة طبية ℞
              </label>
              {rxOn && (
                <div className="mt-3 space-y-3 p-3 rounded border bg-primary/5" data-testid="rx-pad">
                  {rxTemplates.length > 0 && (
                    <div className="p-2 rounded bg-background border">
                      <div className="text-[10px] text-muted-foreground mb-1 font-bold">📋 قوالب جاهزة (اضغط للتحميل):</div>
                      <div className="flex flex-wrap gap-1">
                        {rxTemplates.map(tpl => (
                          <div key={tpl.id} className="inline-flex items-center gap-0.5 border rounded overflow-hidden">
                            <button type="button" onClick={() => loadTemplate(tpl)} className="text-xs px-2 py-1 hover:bg-primary hover:text-primary-foreground transition" data-testid={`tpl-${tpl.id}`}>{tpl.name}</button>
                            <button type="button" onClick={() => delTemplate(tpl)} className="px-1 text-red-500 hover:bg-red-50 text-[10px]" title="حذف القالب">✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div><Label className="text-xs">التشخيص</Label><Input value={rx.diagnosis} onChange={e => setRx({ ...rx, diagnosis: e.target.value })} placeholder="مثال: التهاب حلق حاد" data-testid="rx-dx" /></div>
                  <div>
                    <Label className="text-xs">الأدوية</Label>
                    <div className="space-y-1.5">
                      {rx.drugs.map((d, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1.5" data-testid={`drug-row-${i}`}>
                          <Input value={d.name} onChange={e => updDrug(i, { name: e.target.value })} placeholder="اسم الدواء" className="text-sm" />
                          <Input value={d.dosage} onChange={e => updDrug(i, { dosage: e.target.value })} placeholder="الجرعة (500mg×2/يوم)" className="text-sm" />
                          <Input value={d.duration} onChange={e => updDrug(i, { duration: e.target.value })} placeholder="المدة (5 أيام)" className="text-sm" />
                          <Input value={d.notes} onChange={e => updDrug(i, { notes: e.target.value })} placeholder="ملاحظات (بعد الأكل)" className="text-sm" />
                          <Button size="icon" variant="ghost" className="h-9 w-9 text-red-500" onClick={() => rmDrug(i)} disabled={rx.drugs.length === 1}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" variant="outline" onClick={addDrug} className="mt-2 h-7 text-xs" data-testid="add-drug-btn"><Plus className="w-3 h-3 me-1" /> دواء آخر</Button>
                  </div>
                  <div><Label className="text-xs">تعليمات إضافية</Label><Textarea rows={2} value={rx.notes} onChange={e => setRx({ ...rx, notes: e.target.value })} placeholder="نصائح للمريض..." /></div>
                  <div className="pt-2 border-t border-primary/20 flex gap-2 items-center">
                    <Input value={saveTplName} onChange={e => setSaveTplName(e.target.value)} placeholder="اسم القالب (لحفظ الوصفة الحالية)" className="text-xs h-8" data-testid="tpl-name-input" />
                    <Button type="button" size="sm" variant="outline" onClick={saveAsTemplate} className="h-8 text-xs" data-testid="save-tpl-btn">💾 احفظ كقالب</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setStatusDlg(null)}>إلغاء</Button><Button onClick={finishVisit} data-testid="save-visit-btn">حفظ وإنهاء</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout dialog */}
      <Dialog open={!!payDlg} onOpenChange={o => !o && setPayDlg(null)}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" /> قبض رسوم الكشف</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3">
              <div className="p-3 rounded bg-muted/30 text-sm space-y-1">
                <div className="font-bold flex items-center gap-1"><User className="w-3.5 h-3.5" /> {payDlg.patient_name}</div>
                {payDlg.doctor_name && <div className="text-xs text-muted-foreground">د. {payDlg.doctor_name}</div>}
                <div className="text-xs text-muted-foreground">{payDlg.date} {payDlg.time}</div>
              </div>
              <div>
                <Label>الرسوم الكلية</Label>
                <Input type="number" value={payForm.fee_override} onChange={e => setPayForm({ ...payForm, fee_override: e.target.value, paid_amount: payForm.payment_type === "cash" ? e.target.value : payForm.paid_amount })} data-testid="pay-fee" />
              </div>
              <div>
                <Label>طريقة الدفع</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setPayForm({ ...payForm, payment_type: "cash", paid_amount: payForm.fee_override })} className={`p-2 rounded border text-sm ${payForm.payment_type === "cash" ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-bold" : ""}`} data-testid="pay-cash">💵 نقدي</button>
                  <button type="button" onClick={() => setPayForm({ ...payForm, payment_type: "credit", paid_amount: "0" })} className={`p-2 rounded border text-sm ${payForm.payment_type === "credit" ? "border-amber-500 bg-amber-50 text-amber-700 font-bold" : ""}`} data-testid="pay-credit">📋 آجل</button>
                </div>
              </div>
              {payForm.payment_type === "cash" && (
                <div>
                  <Label>المبلغ المستلم</Label>
                  <Input type="number" value={payForm.paid_amount} onChange={e => setPayForm({ ...payForm, paid_amount: e.target.value })} data-testid="pay-received" />
                  {parseFloat(payForm.paid_amount) > parseFloat(payForm.fee_override) && (
                    <div className="text-xs text-emerald-600 mt-1">الباقي: {(parseFloat(payForm.paid_amount) - parseFloat(payForm.fee_override)).toLocaleString("ar-EG")}</div>
                  )}
                </div>
              )}
              <div>
                <Label>ملاحظات (اختياري)</Label>
                <Textarea rows={2} value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPayDlg(null)}>إلغاء</Button><Button onClick={doCheckout} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-checkout"><Receipt className="w-4 h-4 me-1" /> إنشاء فاتورة</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
